import { type AllHTMLAttributes } from 'react';

import { type OptionsType, type OptionType } from '@atlaskit/select';
import { type AutoCompleteItem } from '@atlassian/eoc-auto-complete';
import type { Priority } from '@atlassian/eoc-priority-badge';

export interface Heartbeat {
	alertMessage: string;
	alertPriority: Priority;
	alertTags: string[];
	description: string;
	heartbeatEmailAddress: string;
	interval: number;
	intervalUnit: 'minutes' | 'hours' | 'days';
	lastReceivedAt: number;
	name: string;
	ownerTeamId: string;
	status: HeartbeatStatus;
	willExpireAt: number;
}

export interface HeartbeatsResponse {
	heartbeats: Heartbeat[];
	heartbeatEmailDomain: string;
	autoCreateEnabled: boolean;
	limitExceeded?: boolean;
	limit?: number;
}

export type HeartbeatRequest = {
	name: string;
	description: string;
	ownerTeamId: string;
	interval: number;
	intervalUnit: 'minutes' | 'hours' | 'days';
	alertPriority: Priority;
	alertMessage: string;
	alertTags: string[];
};

export type HeartbeatStatus = 'Pending' | 'Responsive' | 'Unresponsive' | 'Off';

export interface StatusOption extends OptionType {
	value: HeartbeatStatus;
}

export type StatusOptions = OptionsType<StatusOption>;

export interface ExpireUnitOption {
	label: Heartbeat['intervalUnit'];
	value: Heartbeat['intervalUnit'];
}

export interface FormData {
	name: string;
	description: string | undefined;
	assignedTeam: AutoCompleteItem;
	expect: {
		interval: AllHTMLAttributes<HTMLInputElement>['value'];
		unit: ExpireUnitOption;
	};
	alertMessage: {
		alertPriority: Priority;
		alertMessage: AllHTMLAttributes<HTMLInputElement>['value'];
	};
	tags:
		| readonly {
				label: string;
				value: string;
		  }[]
		| undefined;
}

export interface UserRightsResponse {
	hasEditRight: boolean;
	hasDeleteRight: boolean;
}

export enum OptionalSupportUrlKeys {
	WHAT_ARE_HEARTBEATS = 'WhatAreHeartbeats',
}

export enum SupportUrlKeys {
	API_DOCUMENTATION = 'APIDocumentation',
	OG_GLOBAL_HEARTBEATS = 'OGGlobalHeartbeats',
	API_PING_URL = 'APIPingURL',
	HEARTBEATS_API = 'HeartbeatsAPI',
	CREATE_API_TOKEN = 'CreateAPIToken',
	EMAIL_INSTRUCTIONS = 'EmailInstructions',
}

export type SupportUrls = {
	[key in SupportUrlKeys]: string;
} & {
	[key in OptionalSupportUrlKeys]?: string;
};
